import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ComposedChart, Area, AreaChart, LabelList 
} from 'recharts';
import { programData, dataGaps, recommendations } from '../data/programData';

// Import individual section components
import CMEScholarshipSection from './sections/CMEScholarshipSection';
import FundingSection from './sections/FundingSection';
import TelehealthSection from './sections/TelehealthSection';
import DataGapsSection from './sections/DataGapsSection';
import RecommendationsSection from './sections/RecommendationsSection';

// Custom Venn Diagram Component for Mental Health Conditions
const VennDiagram = () => {
  const vennData = {
    adhdOnly: 3145,
    anxietyOnly: 1703,
    depressionOnly: 470,
    adhdAnxiety: 521,
    adhdDepression: 116,
    anxietyDepression: 533,
    all3: 209
  };

  const total = Object.values(vennData).reduce((sum, val) => sum + val, 0);

  return (
    <div className="relative w-full h-96 flex items-center justify-center">
      <svg viewBox="0 0 400 300" className="w-full h-full">
        {/* ADHD Circle */}
        <circle
          cx="150"
          cy="120"
          r="80"
          fill="#FF6B6B"
          fillOpacity="0.6"
          stroke="#FF6B6B"
          strokeWidth="2"
        />
        
        {/* Anxiety Circle */}
        <circle
          cx="250"
          cy="120"
          r="80"
          fill="#4ECDC4"
          fillOpacity="0.6"
          stroke="#4ECDC4"
          strokeWidth="2"
        />
        
        {/* Depression Circle */}
        <circle
          cx="200"
          cy="200"
          r="80"
          fill="#FECA57"
          fillOpacity="0.6"
          stroke="#FECA57"
          strokeWidth="2"
        />

        {/* Labels for circles */}
        <text x="110" y="80" textAnchor="middle" className="text-sm font-bold fill-red-700">
          ADHD
        </text>
        <text x="290" y="80" textAnchor="middle" className="text-sm font-bold fill-teal-700">
          Anxiety
        </text>
        <text x="200" y="260" textAnchor="middle" className="text-sm font-bold fill-yellow-700">
          Depression
        </text>

        {/* ADHD Only */}
        <text x="110" y="120" textAnchor="middle" className="text-xs font-semibold">
          {vennData.adhdOnly.toLocaleString()}
        </text>

        {/* Anxiety Only */}
        <text x="290" y="120" textAnchor="middle" className="text-xs font-semibold">
          {vennData.anxietyOnly.toLocaleString()}
        </text>

        {/* Depression Only */}
        <text x="200" y="240" textAnchor="middle" className="text-xs font-semibold">
          {vennData.depressionOnly.toLocaleString()}
        </text>

        {/* ADHD + Anxiety */}
        <text x="200" y="100" textAnchor="middle" className="text-xs font-semibold">
          {vennData.adhdAnxiety.toLocaleString()}
        </text>

        {/* ADHD + Depression */}
        <text x="160" y="180" textAnchor="middle" className="text-xs font-semibold">
          {vennData.adhdDepression.toLocaleString()}
        </text>

        {/* Anxiety + Depression */}
        <text x="240" y="180" textAnchor="middle" className="text-xs font-semibold">
          {vennData.anxietyDepression.toLocaleString()}
        </text>

        {/* All Three */}
        <text x="200" y="150" textAnchor="middle" className="text-xs font-bold">
          {vennData.all3.toLocaleString()}
        </text>

        {/* Total label */}
        <text x="350" y="50" textAnchor="middle" className="text-sm font-bold">
          Total: {total.toLocaleString()}
        </text>
      </svg>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-lg border">
        <h4 className="text-sm font-bold mb-2">Patient Distribution</h4>
        <div className="space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-400 rounded"></div>
            <span>ADHD: {(3145 + 521 + 116 + 209).toLocaleString()} patients</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-teal-400 rounded"></div>
            <span>Anxiety: {(1703 + 521 + 533 + 209).toLocaleString()} patients</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-yellow-400 rounded"></div>
            <span>Depression: {(470 + 116 + 533 + 209).toLocaleString()} patients</span>
          </div>
          <div className="border-t pt-1 mt-2 font-semibold">
            Total: {total.toLocaleString()} patients
          </div>
        </div>
      </div>
    </div>
  );
};

// Shared MetricCard component
const MetricCard = ({ icon: Icon, title, value, subtitle, trend, color = 'blue', isAnimated = true }) => (
  <div className={`transform transition-all duration-500 hover:scale-105 ${isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} p-6 bg-white rounded-lg shadow`}>
    <div className="flex items-center space-x-4">
      <div className={`p-3 rounded-full bg-${color}-100`}>
        {Icon && <Icon className={`h-6 w-6 text-${color}-600`} />}
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        {trend && (
          <span className={`mt-1 px-2 py-1 rounded text-xs ${trend > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {trend > 0 ? '↗' : '↘'} {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  </div>
);

// Overview Section Component
const OverviewSection = ({ isAnimated }) => (
  <div className="space-y-8">
    <div className="text-center mb-8">
      <h1 className="text-4xl font-bold text-gray-900 mb-4">
        Building Bridges Program Effectiveness
      </h1>
      <p className="text-xl text-gray-600 max-w-3xl mx-auto">
        Comprehensive Analysis of ADHD, Anxiety, and Depression Care (2021-2025)<br/>
        <span className="text-sm text-gray-500 italic">Data Source: Building Bridges Program - All Providers</span>
      </p>
      <div className="mt-4 text-lg px-4 py-2 border rounded bg-gray-100 inline-block">
        Building Bridges: {programData.keyMetrics.totalPatients.toLocaleString()} Unique Pediatric Visits • {programData.keyMetrics.adhdDiagnoses.toLocaleString()} ADHD Diagnoses • 6,697 Mental Health Patients
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <MetricCard
        title="Total Patients Served"
        value={`${Math.round(programData.keyMetrics.totalPatients / 1000)}K+`}
        subtitle="Unique pediatric visits (2021-2025)"
        trend={programData.keyMetrics.visitGrowth}
        color="blue"
        isAnimated={isAnimated}
      />
      <MetricCard
        title="ADHD Diagnoses"
        value={programData.keyMetrics.adhdDiagnoses.toLocaleString()}
        subtitle="Consistent 2% identification rate"
        trend={55.2}
        color="purple"
        isAnimated={isAnimated}
      />
      <MetricCard
        title="Medicaid Coverage"
        value={`${programData.keyMetrics.medicaidDominance}%`}
        subtitle="7,344 ADHD patients"
        color="green"
        isAnimated={isAnimated}
      />
      <MetricCard
        title="Telehealth Growth"
        value={`${programData.keyMetrics.telehealthGrowth}%`}
        subtitle="2021-2024 expansion"
        trend={programData.keyMetrics.telehealthGrowth}
        color="orange"
        isAnimated={isAnimated}
      />
    </div>

    <div className="mt-8 p-4 border-l-4 border-blue-200 bg-blue-50">
      <strong>Program Success Indicators:</strong> Consistent ADHD identification, {programData.keyMetrics.visitGrowth}% growth in specialized visits, 
      {programData.keyMetrics.comorbidityRate}% comorbidity management, and expanding telehealth access demonstrate Building Bridges effectiveness.
    </div>
  </div>
);

const ADHDTrendsSection = () => (
  <div className="space-y-6">
    <div className="text-center mb-6">
      <h2 className="text-3xl font-bold text-gray-900">ADHD Program Effectiveness</h2>
      <p className="text-gray-600 mt-2">Demonstrating consistent identification and growth in specialized care</p>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-xl font-semibold mb-2">Annual ADHD Diagnosis Trends</h3>
        <p className="text-sm text-gray-600 mb-4">Actual data showing ADHD diagnosis numbers on each bar</p>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={programData.visitTrends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis yAxisId="left" domain={[0, 33000]} />
            <Tooltip 
              formatter={(value, name, props) => {
                if (value === null) return ['-', name];
                // Only show Total Visits in tooltip since ADHD numbers are displayed as labels
                if (name === "Total Visits") {
                  return [value.toLocaleString(), name];
                }
                return null;
              }}
            />
            <Legend />
            
            {/* Bar chart for total visits with ADHD diagnosis labels */}
            <Bar yAxisId="left" dataKey="totalVisits" fill="#8884d8" name="Total Visits" opacity={0.6}>
              {programData.visitTrends.map((entry, index) => (
                <LabelList 
                  key={index}
                  dataKey="adhdDiagnoses" 
                  position="top" 
                  fill="#ff7300"
                  fontSize={12}
                  fontWeight="bold"
                  formatter={(value) => value ? value.toLocaleString() : ''}
                />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>

    {/* New ADHD Established Follow-up Chart */}
    <div className="mt-8 bg-white p-6 rounded-lg shadow">
      <h3 className="text-xl font-semibold mb-2">ADHD Established Follow-up Trends</h3>
      <p className="text-sm text-gray-600 mb-4">Analysis of established ADHD patient follow-up visits and medical follow-up frequency</p>
      
      <div className="w-full">
        <h4 className="text-lg font-medium mb-4">ADHD Established Patients vs Medical Follow-ups per Year & Follow-up Percentage</h4>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={[
            { year: '2021', established: 2267, medicalFU: 5.52, percentage: 10 },
            { year: '2022', established: 2414, medicalFU: 5.05, percentage: 10 },
            { year: '2023', established: 2801, medicalFU: 4.95, percentage: 11 },
            { year: '2024', established: 2916, medicalFU: 4.57, percentage: 10 },
            { year: 'Jan-May 2025', established: 1410, medicalFU: 5.09, percentage: 7 }
          ]}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" domain={[0, 15]} />
            <Tooltip 
              formatter={(value, name) => {
                if (name === 'ADHD Established Patients') return [value.toLocaleString(), name];
                if (name === 'Average Medical F/U per Year') return [value, name];
                if (name === '% ADHD Established F/U / General Pediatric') return [`${value}%`, name];
                return [value, name];
              }}
            />
            <Legend />
            
            <Bar yAxisId="left" dataKey="established" fill="#10b981" name="ADHD Established Patients" opacity={0.8} />
            <Line 
              yAxisId="right" 
              type="monotone" 
              dataKey="medicalFU" 
              stroke="#f59e0b" 
              strokeWidth={3}
              name="Average Medical F/U per Year"
              dot={{ fill: '#f59e0b', strokeWidth: 2, r: 6 }}
            />
            <Line 
              yAxisId="right" 
              type="monotone" 
              dataKey="percentage" 
              stroke="#8b5cf6" 
              strokeWidth={3}
              name="% ADHD Established F/U / General Pediatric"
              dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 6 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* New Chart: ADHD Established vs General Pediatric */}
      <div className="mt-6">
        <h4 className="text-lg font-medium mb-4">ADHD Established Follow-up vs General Pediatric Volume Comparison</h4>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={[
            { year: '2021', adhdEstablished: 2267, generalPediatric: 22732, percentage: 10 },
            { year: '2022', adhdEstablished: 2414, generalPediatric: 24558, percentage: 10 },
            { year: '2023', adhdEstablished: 2801, generalPediatric: 26142, percentage: 11 },
            { year: '2024', adhdEstablished: 2916, generalPediatric: 29523, percentage: 10 },
            { year: 'Jan-May 2025', adhdEstablished: 1410, generalPediatric: 19411, percentage: 7 }
          ]}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" domain={[0, 15]} />
            <Tooltip 
              formatter={(value, name) => {
                if (name === 'General Pediatric Visits') return [value.toLocaleString(), name];
                if (name === 'ADHD Established F/U') return [value.toLocaleString(), name];
                if (name === 'F/U Percentage') return [`${value}%`, name];
                return [value, name];
              }}
            />
            <Legend />
            
            <Bar yAxisId="left" dataKey="generalPediatric" fill="#3b82f6" name="General Pediatric Visits" opacity={0.6} />
            <Bar yAxisId="left" dataKey="adhdEstablished" fill="#10b981" name="ADHD Established F/U" opacity={0.9} />
            <Line 
              yAxisId="right" 
              type="monotone" 
              dataKey="percentage" 
              stroke="#ef4444" 
              strokeWidth={3}
              name="F/U Percentage"
              dot={{ fill: '#ef4444', strokeWidth: 2, r: 6 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>

    <div className="mt-8 p-4 border-l-4 border-purple-200 bg-purple-50">
      <strong>Clinical Impact:</strong> Early and consistent identification of ADHD enables timely intervention and improved outcomes for pediatric patients.
    </div>
  </div>
);

const ComorbiditySection = () => (
  <div className="space-y-6">
    <div className="text-center mb-6">
      <h2 className="text-3xl font-bold text-gray-900">Integrated Mental Health Care</h2>
      <p className="text-gray-600 mt-2">Addressing complex comorbidity patterns for comprehensive treatment</p>
    </div>

    {/* Venn Diagram Section */}
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-xl font-semibold mb-2">Mental Health Condition Overlap - Venn Diagram</h3>
      <p className="text-sm text-gray-600 mb-4">Visual representation of diagnosis combinations across 6,697 patients</p>
      <VennDiagram />
    </div>

    <div className="grid grid-cols-1 gap-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-xl font-semibold mb-4">Comorbidity Statistics</h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="text-center p-3 bg-red-50 rounded">
            <p className="text-2xl font-bold text-red-600">3,991</p>
            <p className="text-sm text-red-700">Total ADHD</p>
          </div>
          <div className="text-center p-3 bg-teal-50 rounded">
            <p className="text-2xl font-bold text-teal-600">2,966</p>
            <p className="text-sm text-teal-700">Total Anxiety</p>
          </div>
          <div className="text-center p-3 bg-yellow-50 rounded">
            <p className="text-2xl font-bold text-yellow-600">1,328</p>
            <p className="text-sm text-yellow-700">Total Depression</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded">
            <p className="text-2xl font-bold text-purple-600">6,697</p>
            <p className="text-sm text-purple-700">Total Patients</p>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between items-center p-3 bg-green-50 rounded">
            <span className="font-medium">Single Diagnosis</span>
            <span className="px-2 py-1 bg-green-200 text-green-800 rounded text-sm">5,318 patients (79.4%)</span>
          </div>
          <div className="flex justify-between items-center p-3 bg-yellow-50 rounded">
            <span className="font-medium">Dual Comorbidity</span>
            <span className="px-2 py-1 bg-yellow-200 text-yellow-800 rounded text-sm">1,170 patients (17.5%)</span>
          </div>
          <div className="flex justify-between items-center p-3 bg-red-50 rounded">
            <span className="font-medium">Triple Comorbidity</span>
            <span className="px-2 py-1 bg-red-200 text-red-800 rounded text-sm">209 patients (3.1%)</span>
          </div>
        </div>
      </div>
    </div>

    <div className="mt-8 p-4 border-l-4 border-orange-200 bg-orange-50">
      <strong>Integration Success:</strong> 1,379 patients with multiple conditions receive coordinated care, 
      demonstrating the program's effectiveness in addressing complex mental health needs through the Venn diagram analysis.
    </div>

    {/* SSRI Prescription Trends Section */}
    <div className="mt-8">
      <h3 className="text-2xl font-bold text-gray-900 mb-4">SSRI Prescription Trends: Expert vs. Practice</h3>
      <p className="text-gray-600 mb-6">Analysis of SSRI prescribing patterns showing adaptation to provider schedule changes</p>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h4 className="text-lg font-semibold mb-2">SSRI Prescriptions: Expert Provider vs All Others with 2025 Projection</h4>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={[
              { year: 2021, anzalone: 53, others: 8, anzaloneProjected: null },
              { year: 2022, anzalone: 158, others: 34, anzaloneProjected: null },
              { year: 2023, anzalone: 220, others: 106, anzaloneProjected: null },
              { year: 2024, anzalone: 112, others: 133, anzaloneProjected: null },
              { year: 2025, anzalone: 46, others: 34, anzaloneProjected: 110 }
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="anzalone" 
                stroke="#f59e0b" 
                strokeWidth={3}
                name="Expert Provider (actual)"
                dot={{ fill: '#f59e0b', strokeWidth: 2, r: 6 }}
              />
              <Line 
                type="monotone" 
                dataKey="others" 
                stroke="#ef4444" 
                strokeWidth={2}
                strokeDasharray="5 5"
                name="All Others (total)"
                dot={{ fill: '#ef4444', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="anzaloneProjected" 
                stroke="#3b82f6" 
                strokeWidth={3}
                strokeDasharray="5 5"
                name="Expert Provider (projected)"
                dot={{ fill: '#3b82f6', stroke: '#ffffff', strokeWidth: 3, r: 8 }}
                activeDot={{ fill: '#3b82f6', stroke: '#ffffff', strokeWidth: 3, r: 10 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h4 className="text-lg font-semibold mb-2">SSRI Prescriptions: Expert Provider vs All Others (Adjusted by FTE)</h4>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={[
              { year: 2021, anzalonePerFTE: 53, others: 8, othersProjected: null, fte: "1.0 FTE" },
              { year: 2022, anzalonePerFTE: 158, others: 34, othersProjected: null, fte: "1.0 FTE" },
              { year: 2023, anzalonePerFTE: 220, others: 106, othersProjected: null, fte: "1.0 FTE" },
              { year: 2024, anzalonePerFTE: 224, others: 133, othersProjected: null, fte: "0.5 FTE" },
              { year: 2025, anzalonePerFTE: 230, others: 34, othersProjected: 68, fte: "0.2 FTE" }
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip 
                formatter={(value, name, props) => {
                  if (name === "Expert Provider (per FTE)") {
                    return [value, `${name} (${props.payload.fte})`];
                  }
                  return [value, name];
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="anzalonePerFTE" 
                stroke="#f59e0b" 
                strokeWidth={3}
                name="Expert Provider (per FTE)"
                dot={{ fill: '#f59e0b', strokeWidth: 2, r: 6 }}
              />
              <Line 
                type="monotone" 
                dataKey="others" 
                stroke="#ef4444" 
                strokeWidth={2}
                strokeDasharray="5 5"
                name="All Others (actual)"
                dot={{ fill: '#ef4444', strokeWidth: 2, r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="othersProjected" 
                stroke="#3b82f6" 
                strokeWidth={3}
                strokeDasharray="5 5"
                name="All Others (projected)"
                dot={{ fill: '#3b82f6', stroke: '#ffffff', strokeWidth: 3, r: 8 }}
                activeDot={{ fill: '#3b82f6', stroke: '#ffffff', strokeWidth: 3, r: 10 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Clinical and Operational Implications */}
      <div className="mt-6 bg-white p-6 rounded-lg shadow">
        <h4 className="text-lg font-semibold mb-4">Clinical and Operational Implications</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center mb-2">
              <h5 className="font-semibold text-blue-900">Drop in Volume</h5>
            </div>
            <p className="text-sm text-blue-800">
              Expert Provider's SSRI prescriptions declined proportionally with reduced hours, 
              suggesting consistent productivity per unit of time.
            </p>
          </div>
          
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="flex items-center mb-2">
              <h5 className="font-semibold text-green-900">Workforce Shift</h5>
            </div>
            <p className="text-sm text-green-800">
              In 2024–2025, the increase in SSRI prescriptions by other providers implies 
              a successful redistribution of responsibilities or improved provider training.
            </p>
          </div>
          
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center mb-2">
              <h5 className="font-semibold text-purple-900">System Resilience</h5>
            </div>
            <p className="text-sm text-purple-800">
              The continued increase (or maintenance) in total SSRI prescriptions shows 
              the system adapted to provider schedule changes without reducing access.
            </p>
          </div>
        </div>
        
          <div className="mt-4 p-4 border-l-4 border-amber-200 bg-amber-50">
            <strong>Key Insight:</strong> When adjusted for FTE, Expert Provider's per-unit productivity 
            remained consistent at ~220 prescriptions per full-time equivalent, demonstrating 
            expertise sustainability even with reduced schedule.
        </div>
      </div>

      {/* Building Bridges Program Impact and Scope */}
      <div className="mt-6 bg-white p-6 rounded-lg shadow">
        <h4 className="text-lg font-semibold mb-2">Building Bridges Program: Expanding Access Through Primary Care</h4>
        <p className="text-sm text-gray-600 mb-4">
          Addressing pediatric mental health access through primary care provider education and empowerment
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h5 className="font-semibold text-gray-900">Current Challenge: Pediatric Psychology Shortage</h5>
            <div className="bg-red-50 p-4 rounded-lg">
              <ul className="space-y-2 text-sm text-red-800">
                <li>• Limited availability of pediatric psychologists and psychiatrists</li>
                <li>• Long wait times for specialist appointments (often 3-6 months)</li>
                <li>• Geographic barriers in rural and underserved areas</li>
                <li>• Insurance coverage limitations for mental health services</li>
                <li>• Increasing demand for adolescent mental health services post-pandemic</li>
              </ul>
            </div>
          </div>
          
          <div className="space-y-4">
            <h5 className="font-semibold text-gray-900">Building Bridges Solution: Primary Care Integration</h5>
            <div className="bg-green-50 p-4 rounded-lg">
              <ul className="space-y-2 text-sm text-green-800">
                <li>• Train primary care providers in SSRI prescribing for adolescents</li>
                <li>• Provide consultation support from pediatric mental health experts</li>
                <li>• Establish clear protocols for screening and initial treatment</li>
                <li>• Create referral pathways for complex cases</li>
                <li>• Offer continuing education and case-based learning</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-4 bg-blue-50 p-6 rounded-lg">
          <h5 className="font-semibold text-blue-900 mb-4">Potential Impact Analysis</h5>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h6 className="font-medium text-blue-900 mb-2">Access Expansion</h6>
              <p className="text-sm text-blue-800">
                <strong>5-10x increase</strong> in providers capable of prescribing SSRIs for adolescent 
                depression and anxiety in primary care settings
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h6 className="font-medium text-blue-900 mb-2">Wait Time Reduction</h6>
              <p className="text-sm text-blue-800">
                <strong>Immediate to 2-week</strong> access for initial SSRI treatment, 
                compared to the typical 5 to 10 years before these children are referred to a psychiatrist and treated—by which time the child's education and future prospects are often adversely impacted
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h6 className="font-medium text-blue-900 mb-2">Geographic Coverage</h6>
              <p className="text-sm text-blue-800">
                <strong>Rural and underserved</strong> areas gain access to evidence-based 
                mental health treatment through existing primary care infrastructure
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 bg-purple-50 p-6 rounded-lg">
          <h5 className="font-semibold text-purple-900 mb-4">Expert Provider's Role: Expert Consultation Model</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h6 className="font-medium text-purple-900 mb-2">Training and Education</h6>
              <ul className="text-sm text-purple-800 space-y-1">
                <li>• Develop standardized SSRI prescribing protocols</li>
                <li>• Lead case-based learning sessions</li>
                <li>• Provide virtual consultation support</li>
                <li>• Create decision-making tools and resources</li>
              </ul>
            </div>
            <div>
              <h6 className="font-medium text-purple-900 mb-2">Quality Assurance</h6>
              <ul className="text-sm text-purple-800 space-y-1">
                <li>• Monitor prescribing patterns and outcomes</li>
                <li>• Provide feedback on complex cases</li>
                <li>• Ensure safety protocols are followed</li>
                <li>• Support continuous improvement initiatives</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-4 p-4 border-l-4 border-green-200 bg-green-50">
          <strong>Program Vision:</strong> By empowering primary care providers with SSRI prescribing 
          capabilities and expert consultation support, the Building Bridges program can dramatically 
          expand access to mental health treatment for adolescents, addressing the critical shortage 
          of pediatric psychology specialists while maintaining quality and safety standards.
        </div>
      </div>
    </div>
  </div>
);

const BuildingBridgesStoryboard = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [isAnimated, setIsAnimated] = useState(false);

  useEffect(() => {
    setIsAnimated(true);
  }, []);

  const renderContent = () => {
    switch (activeSection) {
      case 'overview': return <OverviewSection isAnimated={isAnimated} />;
      case 'adhd': return <ADHDTrendsSection />;
      case 'comorbidity': return <ComorbiditySection />;
      case 'funding': return <FundingSection />;
      case 'telehealth': return <TelehealthSection />;
      case 'gaps': return <DataGapsSection />;
      case 'recommendations': return <RecommendationsSection />;
      case 'cme': return <CMEScholarshipSection />;
      default: return <OverviewSection isAnimated={isAnimated} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {Object.entries({
              overview: 'Overview',
              adhd: 'ADHD Trends',
              comorbidity: 'Integration',
              funding: 'Funding',
              telehealth: 'Telehealth',
              gaps: 'Data Gaps',
              recommendations: 'Recommendations',
              cme: 'CME',
            }).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                  activeSection === key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="transition-all duration-300">
          {renderContent()}
        </div>

        <footer className="mt-12 pt-8 border-t border-gray-200 text-center text-gray-500">
          <p>Building Bridges Program Analysis • 2021-2025 Data • Generated from Comprehensive Jupyter Analysis</p>
        </footer>
      </div>
    </div>
  );
};

export default BuildingBridgesStoryboard;
